/**
 * Подготовка аватара в браузере: обрезка по центру до квадрата, уменьшение до AVATAR_SIZE и JPEG.
 * Перекодирование через canvas заодно убирает EXIF (геометку, модель телефона) — на сервер уходят
 * только пиксели. Поворот по EXIF браузеры применяют сами при отрисовке <img> в canvas.
 */

export const AVATAR_SIZE = 512;
const AVATAR_QUALITY = 0.88;
/** Фон под прозрачные области PNG (JPEG без альфа-канала) — цвет фона приложения. */
const AVATAR_BACKGROUND = "#0B0E0A";
/** Исходники больше этого не открываем: декодирование съест память телефона. */
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/** Ошибка подготовки картинки; message можно сразу показывать пользователю. */
export class ImageError extends Error {
  constructor(message) {
    super(message);
    this.name = "ImageError";
  }
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageError("Не удалось открыть изображение. Попробуйте фото в JPG или PNG."));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob === "function") canvas.toBlob(resolve, type, quality);
    else resolve(null);
  });
}

/**
 * @param {File} file — выбранный пользователем файл
 * @returns {Promise<Blob>} квадратный JPEG не больше AVATAR_SIZE×AVATAR_SIZE
 * @throws {ImageError}
 */
export async function prepareAvatar(file) {
  if (!file) throw new ImageError("Файл не выбран");
  if (file.type && !file.type.startsWith("image/")) throw new ImageError("Выберите изображение");
  if (file.size > MAX_SOURCE_BYTES) throw new ImageError("Файл слишком большой");

  const img = await loadImage(file);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (!width || !height) throw new ImageError("Не удалось открыть изображение");

  const side = Math.min(width, height);
  const size = Math.min(AVATAR_SIZE, side);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ImageError("Не удалось обработать изображение");
  ctx.fillStyle = AVATAR_BACKGROUND;
  ctx.fillRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, (width - side) / 2, (height - side) / 2, side, side, 0, 0, size, size);

  const blob = await canvasToBlob(canvas, "image/jpeg", AVATAR_QUALITY);
  if (!blob) throw new ImageError("Не удалось обработать изображение");
  return blob;
}
